/**
 * The Console V3 design's chart and cell helpers, as pure functions: the geometry and
 * colours each primitive draws, ported line for line from Aithora ConsoleV3.dc.html so
 * the web console and the phone app draw the same thing. The React components in
 * apps/console/src/ui/charts only turn these into SVG.
 *
 * Only the drawing and the formatting are ported. The design's sample generators
 * (traj, maskedResidents' invented rows, scVal's percentile-to-value guesses, the
 * fixed State/Region/City tier shifts) are not: every figure these helpers draw
 * comes from the server.
 */
import { chart, color } from './tokens/tokens';

// ── Colour for a percentile (design pc(), L18960) ───────────────────────────
/** 75 and up green, 50 and up blue, 40 and up watch grey, under 40 red. */
export function percentileColor(v: number): string {
  return v >= 75 ? '#1F6B52' : v >= 50 ? '#2F5C97' : v >= 40 ? '#5B6B7C' : '#B3261E';
}

// ── Heat cells (design heat()/heatA(), L33407) ──────────────────────────────
export interface HeatCell {
  /** The printed value; '-' when there is none (the design prints a hyphen). */
  v: string;
  bg: string;
  fg: string;
}

/** Tinted heat cell on a cream card (design heat()). */
export function heat(v: number | null): HeatCell {
  if (v === null) return { v: '-', bg: chart.heatNone, fg: '#54606C' };
  let bg: string;
  let fg = '#22272E';
  if (v >= 90) bg = chart.heat90;
  else if (v >= 75) bg = chart.heat75;
  else if (v >= 60) bg = chart.heat60;
  else if (v >= 40) bg = chart.heat40;
  else {
    bg = chart.heat0;
    fg = '#B3261E';
  }
  return { v: String(v), bg, fg };
}

/**
 * Portfolio heatmap palette (design heatA(), spec 9): one hue, five lightness steps, so
 * the order reads for colour-blind readers and in greyscale.
 */
export function heatA(v: number | null): HeatCell {
  if (v === null) return { v: '-', bg: chart.heatNone, fg: '#54606C' };
  if (v >= 90) return { v: String(v), bg: chart.heatA90, fg: '#FFFFFF' };
  if (v >= 75) return { v: String(v), bg: chart.heatA75, fg: '#FFFFFF' };
  if (v >= 60) return { v: String(v), bg: chart.heatA60, fg: '#22272E' };
  if (v >= 40) return { v: String(v), bg: chart.heatA40, fg: '#22272E' };
  return { v: String(v), bg: chart.heatA0, fg: '#B3261E' };
}

// ── trendChart (L33911): a 240x56 sparkline with an area and an end dot ─────
export interface TrendGeometry {
  width: number;
  height: number;
  line: string;
  area: string;
  end: { cx: number; cy: number };
}

export function trendGeometry(pts: readonly number[]): TrendGeometry | null {
  if (pts.length < 2) return null;
  const w = 240;
  const ht = 56;
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const rng = max - min || 1;
  const x = (i: number) => i * (w / (pts.length - 1));
  const y = (p: number) => ht - 6 - ((p - min) / rng) * (ht - 16);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p).toFixed(1)}`).join(' ');
  const area = `${line} L${w} ${ht} L0 ${ht} Z`;
  return { width: w, height: ht, line, area, end: { cx: x(pts.length - 1), cy: y(pts[pts.length - 1]) } };
}

// ── convBars (L18899): bars scaled to the 2nd-highest, a dashed median ──────
export interface ConvBarsGeometry {
  width: number;
  height: number;
  medianY: number;
  bars: { x: number; y: number; width: number; height: number; fill: string; clipped: boolean }[];
}

export function convBarsGeometry(vals: readonly number[]): ConvBarsGeometry {
  const W = 1000;
  const H = 132;
  const mB = 4;
  const mT = 10;
  const ranked = vals.slice().sort((a, b) => b - a);
  const sorted = vals.slice().sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)] ?? 0;
  // scale to the 2nd-highest so one backfill spike cannot flatten the rest
  const scaleMax = Math.max(ranked[1] || 1, 1);
  const n = Math.max(vals.length, 1);
  const bw = (W / n) * 0.8;
  const gap = (W / n) * 0.2;
  const y = (v: number) => H - mB - (Math.min(v, scaleMax) / scaleMax) * (H - mB - mT);
  return {
    width: W,
    height: H,
    medianY: y(med),
    bars: vals.map((v, i) => {
      const clipped = v > scaleMax;
      const top = y(v);
      return { x: i * (bw + gap) + gap / 2, y: top, width: bw, height: Math.max(1.5, H - mB - top), fill: clipped ? color.accent : chart.bar, clipped };
    }),
  };
}

// ── distStrip (L18926): where each facility sits on 0-100, P10-P90 band ─────
export interface DistStripGeometry {
  width: number;
  height: number;
  track: { x: number; y: number; width: number; height: number };
  band: { x: number; y: number; width: number; height: number };
  median: { x: number; y1: number; y2: number };
  dots: { key: number; cx: number; cy: number; r: number; fill: string; strokeWidth: number; opacity: number }[];
}

export function distStripGeometry(vals: readonly (number | null)[], highlightIdx: number | null = null): DistStripGeometry {
  const W = 300;
  const H = 30;
  const mX = 4;
  const cy = 17;
  const x = (v: number) => mX + (v / 100) * (W - mX * 2);
  const dots: DistStripGeometry['dots'] = [];
  vals.forEach((v, i) => {
    if (v === null) return;
    const on = i === highlightIdx;
    dots.push({ key: i, cx: x(v), cy, r: on ? 5 : 3.4, fill: on ? '#22272E' : percentileColor(v), strokeWidth: on ? 1.6 : 1, opacity: on ? 1 : 0.85 });
  });
  return {
    width: W,
    height: H,
    track: { x: mX, y: cy - 3.5, width: W - mX * 2, height: 7 },
    band: { x: x(10), y: cy - 3.5, width: x(90) - x(10), height: 7 },
    median: { x: x(50), y1: cy - 7, y2: cy + 7 },
    dots,
  };
}

// ── cogRing (L33317): a 78px progress ring with the score in the middle ─────
export function cogRingGeometry(score: number, outOf: number): { r: number; circumference: number; dashOffset: number } {
  const R = 30;
  const C = 2 * Math.PI * R;
  const frac = Math.max(0, Math.min(1, score / outOf));
  return { r: R, circumference: C, dashOffset: C * (1 - frac) };
}

// ── lineChart (L33938): quarters on x, 0-100 on y, peer median, +30d ────────
export interface LineSeries {
  key: string;
  label: string;
  color: string;
  /** One value per label (quarter). */
  pts: readonly number[];
  /** Selected: drawn thicker; the others dim when any series is selected. */
  on?: boolean;
}

export interface LineChartMarker {
  /** Index into the labels. */
  at: number;
  label: string;
  /** The tooltip (the design's <title>). */
  note: string;
}

export interface LineChartLayout {
  W: number;
  H: number;
  mL: number;
  mR: number;
  mT: number;
  mB: number;
  lo: number;
  hi: number;
  ticks: number[];
  x: (i: number) => number;
  y: (v: number) => number;
  /** Where each line trends in 30 days (MODELED: last three quarters' slope). */
  forecast: number[];
  forecastX: number;
  envelope: string | null;
  paths: string[];
  /** Index from a pointer x in viewBox units, clamped. */
  indexAt: (vx: number) => number;
}

export function lineChartLayout(series: readonly LineSeries[], labels: readonly string[], peerMedian: number | null): LineChartLayout {
  const W = 1000;
  const H = 540;
  const mL = 44;
  const mR = 96;
  const mT = 28;
  const mB = 36;
  const n = labels.length;
  const fc = series.map((s) => Math.max(0, Math.min(100, s.pts[n - 1] + (s.pts[n - 1] - s.pts[Math.max(0, n - 4)]) / 3 / 3)));
  let dMin = 100;
  let dMax = 0;
  series.forEach((s) => s.pts.forEach((p) => {
    if (p < dMin) dMin = p;
    if (p > dMax) dMax = p;
  }));
  fc.forEach((p) => {
    if (p < dMin) dMin = p;
    if (p > dMax) dMax = p;
  });
  if (peerMedian !== null) {
    dMin = Math.min(dMin, peerMedian);
    dMax = Math.max(dMax, peerMedian);
  }
  const lo = Math.max(0, Math.floor((dMin - 4) / 5) * 5);
  const hi = Math.min(100, Math.ceil((dMax + 4) / 5) * 5);
  const ticks: number[] = [];
  const step = (hi - lo) / 4;
  for (let i = 0; i <= 4; i++) ticks.push(Math.round(lo + step * i));
  const span = (W - mL - mR) / Math.max(1, n - 1);
  const x = (i: number) => mL + i * span;
  const y = (v: number) => mT + (1 - (v - lo) / (hi - lo || 1)) * (H - mT - mB);
  let envelope: string | null = null;
  if (series.length > 1) {
    const mins: number[] = [];
    const maxs: number[] = [];
    for (let i = 0; i < n; i++) {
      const col = series.map((s) => s.pts[i]);
      mins.push(Math.min(...col));
      maxs.push(Math.max(...col));
    }
    envelope = `${maxs.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')} ${mins
      .slice()
      .reverse()
      .map((v, i) => `L${x(n - 1 - i).toFixed(1)} ${y(v).toFixed(1)}`)
      .join(' ')} Z`;
  }
  const paths = series.map((s) => s.pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p).toFixed(1)}`).join(' '));
  const indexAt = (vx: number) => Math.max(0, Math.min(n - 1, Math.round((vx - mL) / span)));
  return { W, H, mL, mR, mT, mB, lo, hi, ticks, x, y, forecast: fc, forecastX: x(n - 1) + 62, envelope, paths, indexAt };
}

// ── Count-up (design benchEnter, L33424): numbers count to the printed string ─
/**
 * The frame of a count-up at progress p (0..1, cubic ease-out): the first number in
 * `target` is replaced by its eased value, with the same decimals; at p = 1 it is the
 * target exactly. Returns null when the text has no number to count.
 */
export function countUpFrame(target: string, p: number): string | null {
  const m = target.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  if (p >= 1) return target;
  const to = parseFloat(m[0]);
  const dp = m[1] ? m[1].length - 1 : 0;
  const e = 1 - Math.pow(1 - Math.max(0, p), 3);
  return target.replace(m[0], (to * e).toFixed(dp));
}
/** The design's count-up length in ms. */
export const COUNT_UP_MS = 850;

// ── CSV (design exportCsv, L33343) ──────────────────────────────────────────
/** Every cell quoted, quotes doubled, rows joined with \n, like the design's export. */
export function toCsv(cols: readonly string[], rows: readonly (readonly unknown[])[]): string {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
}

// ── Masked resident row (design maskedResidents, L33354) ────────────────────
/** A real resident as the server gives it for the cross-facility drill. */
export interface ResidentForMasking {
  /** The server's stable resident code (R-<ccn tail>-<n>), never the name. */
  code: string;
  /** Minimum necessary: initials only. */
  initials: string;
  room: string | null;
  mood: string | null;
  openAlerts: number;
  /** Ready for Atlas (consent and device in place). */
  ready: boolean;
}

export interface MaskedResidentRow {
  code: string;
  initials: string;
  room: string;
  mood: string;
  alerts: string;
  alertColor: string;
  ready: 'Ready' | 'Blocked';
  readyColor: string;
}

/** The design's masked row shape and colours, from a real (already masked) row. */
export function maskResidentRow(r: ResidentForMasking): MaskedResidentRow {
  return {
    code: r.code,
    initials: r.initials,
    room: r.room ?? '-',
    mood: r.mood ?? '-',
    alerts: r.openAlerts ? String(r.openAlerts) : '-',
    alertColor: r.openAlerts ? '#B3261E' : '#54606C',
    ready: r.ready ? 'Ready' : 'Blocked',
    readyColor: r.ready ? '#1F6B52' : '#5B6B7C',
  };
}

// ── Metric formats (design scVal, L33378): the formats only ─────────────────
export type MetricKind =
  | 'rating' | 'hprd' | 'rnhprd' | 'nturn' | 'rnturn' | 'survey' | 'severe' | 'penalty' | 'fines'
  | 'occ' | 'rev' | 'margin' | 'hosp' | 'falls' | 'readmit' | 'capture';

/**
 * A real metric value printed the way the design prints that metric. `value` is the
 * measured figure (a rating, hours, a percent, dollars), never a percentile.
 * Missing values print the en dash.
 */
export function formatMetric(kind: MetricKind | string, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  const r = (n: number, d: number) => n.toFixed(d);
  switch (kind) {
    case 'rating': return r(value, 2);
    case 'hprd': return r(value, 2);
    case 'rnhprd': return r(value, 2);
    case 'nturn': return `${r(value, 1)}%`;
    case 'rnturn': return `${r(value, 1)}%`;
    case 'survey': return r(value, 1);
    case 'severe': return String(Math.round(value));
    case 'penalty': return String(Math.round(value));
    case 'fines': return value <= 0 ? '$0' : `$${Math.round(value).toLocaleString('en-US')}`;
    case 'occ': return `${Math.round(value)}%`;
    case 'rev': return `$${Math.round(value / 1000)}K`;
    case 'margin': return `${r(value, 1)}%`;
    case 'hosp': return `${r(value, 1)} /1k RD`;
    case 'falls': return `${r(value, 1)}%`;
    case 'readmit': return `${r(value, 1)}%`;
    case 'capture': return `${r(value, 1)}%`;
    default: return String(value);
  }
}

/** 1st, 2nd, 3rd, 11th, 22nd (design ord()). */
export function ordinal(v: number): string {
  const r10 = v % 10;
  const r100 = v % 100;
  const suf = r10 === 1 && r100 !== 11 ? 'st' : r10 === 2 && r100 !== 12 ? 'nd' : r10 === 3 && r100 !== 13 ? 'rd' : 'th';
  return `${v}${suf}`;
}

// ── Inline style strings ────────────────────────────────────────────────────
/**
 * The design writes styles as CSS strings ("display:flex;gap:10px;"). This turns one
 * into a React style object so a porter can paste the design's string unchanged:
 * `style={css('display:inline-flex;height:32px;')}`. Custom properties keep their name.
 */
export function cssToStyle(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  // split on ; outside parentheses (rgba(...), url(...))
  let depth = 0;
  let cur = '';
  const parts: string[] = [];
  for (const ch of s) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ';' && depth === 0) {
      parts.push(cur);
      cur = '';
    } else cur += ch;
  }
  parts.push(cur);
  for (const part of parts) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const prop = part.slice(0, i).trim();
    const value = part.slice(i + 1).trim();
    if (!prop || !value) continue;
    const key = prop.startsWith('--') ? prop : prop.replace(/^-ms-/, 'ms-').replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    out[key] = value;
  }
  return out;
}

// ── Her week bars (V6 point-of-care.js family week, pocLayer / pwLayer) ──────────
/** One drawn row of Her week: today's figure and a bar a day. */
export interface WeekBarsView {
  /** Today's value with its unit ("80%", "1,200 mL"), "None yet" when nothing is charted
   *  today, or "Not tracked yet" when Aithora records none of this. */
  now: string;
  /** One per day, oldest first: the bar's height in percent (the design's floor of 8), or
   *  null on a day with nothing recorded. `today` marks the last bar (the darker one). */
  bars: Array<{ pct: number | null; today: boolean }>;
}

/**
 * The design's bars for a Her week row: height = value over the row's top (100 for a
 * percentage, else the week's own highest value), at least 8%, today's bar darker. A day
 * with nothing recorded has no bar: it is never drawn as zero.
 */
export function weekBars(row: { unit: string; values: Array<number | null>; tracked: boolean }): WeekBarsView {
  if (!row.tracked) return { now: 'Not tracked yet', bars: [] };
  const nums = row.values.filter((v): v is number => v !== null && Number.isFinite(v));
  const top = row.unit === '%' ? 100 : Math.max(1, ...nums);
  const last = row.values.length - 1;
  const today = row.values[last] ?? null;
  const unit = row.unit === '%' ? '%' : row.unit ? ` ${row.unit}` : '';
  return {
    now: today === null ? 'None yet' : `${today.toLocaleString('en-US')}${unit}`,
    bars: row.values.map((v, i) => ({ pct: v === null ? null : Math.max(8, Math.min(100, Math.round((v / top) * 100))), today: i === last })),
  };
}
